import {
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  forwardRef,
  useEffect,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { sportMeta } from "@/lib/sports";

export function Spinner() {
  return <div className="spinner" role="status" aria-label="Загрузка" />;
}

export function Alert({
  kind = "info",
  children,
  items,
}: {
  kind?: "info" | "error" | "success" | "warning";
  children?: ReactNode;
  items?: string[];
}) {
  return (
    <div className={`alert alert--${kind}`} role={kind === "error" ? "alert" : undefined}>
      {children}
      {items && items.length > 0 && (
        <ul>
          {items.map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: ReactNode;
  error?: string;
  hint?: string;
  aside?: ReactNode;
  /** кнопка внутри поля справа, например «Сгенерировать» */
  action?: ReactNode;
}

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, error, hint, aside, action, className, id, ...rest },
  ref,
) {
  const inputId =
    id ?? (typeof label === "string" ? label.toLowerCase().replace(/\s+/g, "-") : undefined);
  return (
    <div className="field">
      <label className="field__label" htmlFor={inputId}>
        <span>{label}</span>
        {aside}
      </label>
      {action ? (
        <div className="input-wrap input-wrap--action">
          <input
            ref={ref}
            id={inputId}
            className={`input ${error ? "input--error" : ""} ${className ?? ""}`}
            {...rest}
          />
          {action}
        </div>
      ) : (
        <input
          ref={ref}
          id={inputId}
          className={`input ${error ? "input--error" : ""} ${className ?? ""}`}
          {...rest}
        />
      )}
      {error ? (
        <span className="field__error">{error}</span>
      ) : hint ? (
        <span className="field__hint">{hint}</span>
      ) : null}
    </div>
  );
});

export const PasswordField = forwardRef<HTMLInputElement, FieldProps>(function PasswordField(
  { label, error, hint, aside, id, ...rest },
  ref,
) {
  const [show, setShow] = useState(false);
  const inputId = id ?? "password";
  return (
    <div className="field">
      <label className="field__label" htmlFor={inputId}>
        <span>{label}</span>
        {aside}
      </label>
      <div className="input-wrap">
        <input
          ref={ref}
          id={inputId}
          type={show ? "text" : "password"}
          className={`input ${error ? "input--error" : ""}`}
          {...rest}
        />
        <button
          type="button"
          className="input-wrap__btn"
          aria-label={show ? "Скрыть пароль" : "Показать пароль"}
          onClick={() => setShow((s) => !s)}
        >
          {show ? <EyeOff /> : <Eye />}
        </button>
      </div>
      {error ? (
        <span className="field__error">{error}</span>
      ) : hint ? (
        <span className="field__hint">{hint}</span>
      ) : null}
    </div>
  );
});

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: ReactNode;
}
export function Select({ label, className, children, ...rest }: SelectProps) {
  const el = (
    <select className={`select ${className ?? ""}`} {...rest}>
      {children}
    </select>
  );
  if (!label) return el;
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {el}
    </label>
  );
}

function Eye() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function EyeOff() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

export function Logo({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" aria-hidden="true">
      <rect x="2" y="2" width="36" height="36" rx="10" stroke="#9DBDF5" strokeWidth="3" />
      <path
        d="M12 21l6 6 11-13"
        stroke="#9DBDF5"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Иконки видов спорта: мяч, шайба, баскетбольный мяч */
export function SportIcon({ sport, size = 18 }: { sport: string; size?: number }) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  if (sport === "hockey")
    return (
      <svg {...common}>
        <ellipse cx="12" cy="9" rx="8" ry="3.5" />
        <path d="M4 9v5.5c0 1.9 3.6 3.5 8 3.5s8-1.6 8-3.5V9" />
      </svg>
    );
  if (sport === "basketball")
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
      </svg>
    );
  return (
    <svg {...common}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7l4.5 3.3-1.7 5.4H9.2L7.5 10.3z" />
      <path d="M12 3v4M7.5 10.3L4 9M16.5 10.3L20 9M9.2 15.7L7 19M14.8 15.7L17 19" />
    </svg>
  );
}

export function SportBadge({ sport, size = "md" }: { sport: string; size?: "md" | "sm" }) {
  const meta = sportMeta(sport);
  return (
    <span
      className={`sport-badge ${size === "sm" ? "sport-badge--sm" : ""}`}
      style={{ background: meta.color }}
      title={meta.name}
    >
      <SportIcon sport={sport} size={size === "sm" ? 14 : 18} />
    </span>
  );
}

export function SportChip({ sport }: { sport: string }) {
  const meta = sportMeta(sport);
  return (
    <span className="sport-chip" style={{ background: meta.soft, color: meta.color }}>
      <SportBadge sport={sport} size="sm" />
      {meta.name}
    </span>
  );
}

export function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="stat">
      <span className="stat__label">{label}</span>
      <span className="stat__value">
        {value} {sub && <small>{sub}</small>}
      </span>
    </div>
  );
}

export function PageHeader({
  eyebrow,
  eyebrowTo,
  title,
  sport,
  actions,
}: {
  eyebrow?: ReactNode;
  eyebrowTo?: string;
  title: ReactNode;
  sport?: string;
  actions?: ReactNode;
}) {
  const color = sport ? sportMeta(sport).color : "var(--primary)";
  return (
    <div className="page-header">
      <div className="page-header__title">
        {eyebrow &&
          (eyebrowTo ? (
            <a className="eyebrow" style={{ color }} href={eyebrowTo}>
              {eyebrow}
            </a>
          ) : (
            <span className="eyebrow" style={{ color }}>
              {eyebrow}
            </span>
          ))}
        <h1>
          {sport && <SportBadge sport={sport} />}
          <span>{title}</span>
        </h1>
      </div>
      {actions && <div className="page-header__actions">{actions}</div>}
    </div>
  );
}

export function Transfers({
  leftStarters,
  leftBench,
  limitStarters,
  limitBench,
}: {
  leftStarters: number | null;
  leftBench: number | null;
  limitStarters: number;
  limitBench: number;
}) {
  if (leftStarters === null || leftBench === null)
    return <span className="small muted">первый тур команды: замены без ограничений</span>;
  const dots = (left: number, limit: number) => (
    <span className="transfers__dots" aria-hidden="true">
      {Array.from({ length: limit }, (_, i) => (
        <span key={i} className={`transfers__dot ${i < left ? "transfers__dot--left" : ""}`} />
      ))}
    </span>
  );
  return (
    <div className="transfers">
      <div className="transfers__item">
        <span
          className="stat__value"
          style={{ color: leftStarters < 0 ? "var(--accent)" : undefined }}
        >
          {leftStarters} <small>в основу</small>
        </span>
        {dots(Math.max(0, leftStarters), limitStarters)}
      </div>
      <div className="transfers__item">
        <span
          className="stat__value"
          style={{ color: leftBench < 0 ? "var(--accent)" : undefined }}
        >
          {leftBench} <small>в запас</small>
        </span>
        {dots(Math.max(0, leftBench), limitBench)}
      </div>
    </div>
  );
}

function ShieldIcon({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6l8-3z" />
    </svg>
  );
}

/** Отметка суперадмина: щит на аватаре (compact) или плашка с подписью */
export function SuperadminBadge({ compact = false }: { compact?: boolean }) {
  if (compact)
    return (
      <span className="superadmin-dot" title="Суперадмин" aria-label="Суперадмин">
        <ShieldIcon size={11} />
      </span>
    );
  return (
    <span className="superadmin-chip">
      <ShieldIcon size={13} />
      <span>Суперадмин</span>
    </span>
  );
}

/**
 * Лист снизу экрана для телефона: затемнение, закрытие по тапу мимо, по Escape и
 * кнопке «Назад» в шапке листа. Прокрутка страницы под листом блокируется.
 */
export function BottomSheet({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="sheet">
      <div className="sheet__backdrop" onClick={onClose} aria-hidden="true" />
      <div
        className="sheet__panel"
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
      >
        <div className="sheet__head">
          <span className="sheet__grip" aria-hidden="true" />
          {title && <div className="sheet__title">{title}</div>}
          <button type="button" className="sheet__close" aria-label="Закрыть" onClick={onClose}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <div className="sheet__body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
